import {
  Autocomplete,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { motion, type Variants } from "motion/react";
import { withClientorProjects } from "HoC/withClientorProjects";
import logo from "Assets/Images/incendium-colour-logo.png";
import Image from "mui-image";
import withFromTo from "HoC/withFromToProvider";
import { useCallback, useMemo, useState } from "react";
import { ProjectClientSelector } from "features/leftMenu";
import { useSelectedProject } from "Hooks";
import { useIsMobile } from "Hooks/useIsMobile";
import DesktopRequiredScreen from "Components/DesktopRequiredScreen/DesktopRequiredScreen";
import MobileDashboardNoticeDialog from "features/project/components/MobileDashboardNoticeDialog";
import { DashboardCustomize, Insights, Settings } from "@mui/icons-material";
import DashboardSettings from "features/dashboard/components/DahboardSettings";
import DashboardProfileDialog from "features/dashboard/components/DashboardProfileDialog";
import { useDashboardProfiles } from "features/dashboard/hooks/useDashboardProfiles";
import {
  DASHBOARD_PRESET_PROFILES,
  DashboardProfile,
  DashboardReportId,
  getReportComponent,
} from "features/dashboard/constants/dashboardReports";
import AttributionProvider, {
  useAttributionContext,
} from "Providers/AttributionProvider";
import { ApiChartAttribute, ApiAttributionModel } from "@incendium/api";
import QuickInsightsDrawer from "features/dashboard/components/QuickInsightsDrawer";
import { useUser } from "Hooks/useUser";
import { DatePickerDropdown } from "features/analytics";
import AnalyticsAttributionSwitcher from "features/analytics/components/AnalyticsAttributionSwitcher";
import { CallbackOrVal } from "Interfaces";
import { useLayoutContext } from "Providers/LayoutProvider";
import StandardChartsProvider from "features/chartLibrary/providers/StandardChartsProvider";

const easeOut = [0.25, 0.1, 0.25, 1] as const;

const pageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.04,
    },
  },
};

const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: easeOut },
  },
};

const tabButtonVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.32,
      ease: easeOut,
      delay: 0.12 + i * 0.045,
    },
  }),
};

function DashboardProfileAutocomplete({
  activeProfile,
  customProfiles,
  disabled,
  onChange,
  sx,
}: {
  activeProfile: DashboardProfile;
  customProfiles: DashboardProfile[];
  disabled?: boolean;
  onChange: (profileId: string) => void;
  sx?: object;
}) {
  const options = useMemo(
    () => [...DASHBOARD_PRESET_PROFILES, ...customProfiles],
    [customProfiles],
  );

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      disableClearable
      options={options}
      value={activeProfile}
      onChange={(_event, value) => {
        if (value?.id) {
          onChange(value.id);
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.name}
      groupBy={(option) => (option.isPreset ? "Presets" : "Your dashboards")}
      renderInput={(params) => <TextField {...params} />}
      sx={sx}
    />
  );
}

function ProjectDashboardInner() {
  const { user } = useUser();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const { selectedProject } = useSelectedProject();
  const {
    isLoaded,
    isSaving,
    needsSetup,
    activeProfile,
    customProfiles,
    visibleReports,
    activeTabId: activeTab,
    selectProfile,
    applyProfile,
    setActiveTabId,
  } = useDashboardProfiles();
  const showReports = isLoaded && !isSaving;
  const { model, setModel } = useAttributionContext();
  const [selectedAttributes, setSelectedAttributes] = useState<
    ApiChartAttribute[]
  >([]);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const { setQuickInsightsOpen } = useLayoutContext();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileQuickInsightsOpen, setMobileQuickInsightsOpen] = useState(false);

  const handleProfileChange = useCallback(
    (profileId: string) => {
      selectProfile(profileId);
    },
    [selectProfile],
  );

  const handleProfileApply = useCallback(
    async (profile: DashboardProfile) => {
      await applyProfile(profile);
    },
    [applyProfile],
  );

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    value: string | null,
  ) => {
    if (!value) {
      return;
    }
    setActiveTabId(value as DashboardReportId);
  };

  const ActiveReportComponent = useMemo(
    () => getReportComponent(activeTab),
    [activeTab],
  );

  const activeOption = useMemo(() => {
    return settingsOpen
      ? "settings"
      : mobileQuickInsightsOpen
        ? "quick-insights"
        : undefined;
  }, [settingsOpen, mobileQuickInsightsOpen]);

  const onAttributionChange = useCallback(
    (v: CallbackOrVal<ApiAttributionModel[]>) => {
      setModel(v[0] || ApiAttributionModel.EBA);
    },
    [setModel],
  );

  const toggleDesktopQuickInsights = useCallback(() => {
    setQuickInsightsOpen((open) => !open);
    setSettingsOpen(false);
  }, [setQuickInsightsOpen]);

  const toggleMobileQuickInsights = useCallback(() => {
    setMobileQuickInsightsOpen((open) => !open);
    setSettingsOpen(false);
  }, []);

  if (!selectedProject) {
    return (
      <DesktopRequiredScreen>
        <>
          <Typography variant="subtitle1" mb={2}>
            To get started, please select a project
          </Typography>

          <Box
            sx={{
              backgroundColor: theme.palette.secondary.main,
              borderRadius: `${theme.shape.borderRadius}px`,
            }}
          >
            <ProjectClientSelector />
          </Box>
        </>
      </DesktopRequiredScreen>
    );
  }

  return (
    <Box
      component={motion.div}
      variants={pageVariants}
      initial="hidden"
      animate="show"
      style={{ height: "100dvh" }}
      pb={{ xs: 9, md: 13 }}
    >
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box component={motion.div} variants={fadeUpVariants} px={1} py={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            component={motion.div}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.45, ease: easeOut }}
          >
            <Typography
              variant="h2"
              sx={{ fontSize: 28, display: { xs: "none", md: "block" } }}
            >
              Welcome{" "}
              <Typography
                fontSize={32}
                variant="h1"
                display="inline"
                color="primary.main"
              >
                {user?.name}
              </Typography>
            </Typography>
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <Image
                width={160}
                src={logo}
                duration={50}
                fit={"contain"}
                objectPosition={"left"}
              />
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ display: { xs: "flex", md: "none" } }}
            >
              <DashboardProfileAutocomplete
                activeProfile={activeProfile}
                customProfiles={customProfiles}
                disabled={!showReports}
                onChange={handleProfileChange}
                sx={{ minWidth: 140, maxWidth: 180 }}
              />
              <Tooltip title="Customize dashboard">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!showReports}
                    onClick={() => setProfileDialogOpen(true)}
                  >
                    <DashboardCustomize fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Box>
                <AnalyticsAttributionSwitcher
                  collapsible
                  models={[model]}
                  setModels={onAttributionChange}
                  withBg
                />
              </Box>
              <DatePickerDropdown size="small" label="select date range" />
            </Stack>
          </Stack>
          <Stack
            mt={2}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              width: "100%",
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflowX: "auto",
                overflowY: "hidden",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <ToggleButtonGroup
                value={showReports ? activeTab : null}
                exclusive
                color="primary"
                onChange={handleTabChange}
                disabled={!showReports}
                sx={{
                  display: "inline-flex",
                  flexWrap: "nowrap",
                  flexShrink: 0,
                  width: "max-content",
                  maxWidth: "none",
                }}
              >
                {(showReports ? visibleReports : []).map((tab, index) => (
                  <ToggleButton
                    key={tab.id}
                    value={tab.id}
                    component={motion.button}
                    custom={index}
                    variants={tabButtonVariants}
                    initial="hidden"
                    animate="show"
                    whileTap={{
                      scale: 0.96,
                      transition: {
                        type: "spring",
                        stiffness: 420,
                        damping: 28,
                      },
                    }}
                    sx={{
                      minWidth: 100,
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" }, flexShrink: 0 }}
            >
              <DashboardProfileAutocomplete
                activeProfile={activeProfile}
                customProfiles={customProfiles}
                disabled={!showReports}
                onChange={handleProfileChange}
                sx={{ minWidth: 220 }}
              />
              <Tooltip title="Customize dashboard">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    disabled={!showReports}
                    onClick={() => setProfileDialogOpen(true)}
                  >
                    <DashboardCustomize fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                startIcon={<Insights />}
                disabled={!showReports}
                onClick={toggleDesktopQuickInsights}
              >
                View Quick Insights
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box
          component={motion.div}
          variants={fadeUpVariants}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {!showReports ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ width: "100%", height: "100%", minHeight: 240 }}
            >
              <CircularProgress />
            </Stack>
          ) : (
            <Stack
              key={`${activeProfile.id}-${activeTab}`}
              spacing={2}
              p={1}
              pb={2}
              flexWrap="nowrap"
              sx={{ width: "100%", height: "100%" }}
            >
              {ActiveReportComponent && (
                <ActiveReportComponent
                  attributes={selectedAttributes}
                  reportId={activeTab}
                />
              )}
            </Stack>
          )}
        </Box>
      </Box>
      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          display: { xs: "block", md: "none" },
        }}
        elevation={3}
      >
        <BottomNavigation showLabels value={activeOption}>
          <BottomNavigationAction
            value="quick-insights"
            label="Quick Insights"
            icon={<Insights />}
            onClick={toggleMobileQuickInsights}
          />
          <BottomNavigationAction
            value="settings"
            label="Settings"
            icon={<Settings />}
            onClick={() => {
              setSettingsOpen(!settingsOpen);
              setMobileQuickInsightsOpen(false);
            }}
          />
        </BottomNavigation>
      </Paper>
      <DashboardSettings
        model={model}
        setModel={setModel}
        selectedAttributes={selectedAttributes}
        setSelectedAttributes={setSelectedAttributes}
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />
      <DashboardProfileDialog
        open={profileDialogOpen || needsSetup}
        onClose={() => {
          if (!needsSetup) {
            setProfileDialogOpen(false);
          }
        }}
        onApply={handleProfileApply}
        activeProfile={activeProfile}
        customProfiles={customProfiles}
        required={needsSetup}
      />
      {isMobile && (
        <QuickInsightsDrawer
          open={mobileQuickInsightsOpen}
          onClose={() => setMobileQuickInsightsOpen(false)}
        />
      )}
      <MobileDashboardNoticeDialog isMobile={isMobile} />
    </Box>
  );
}

function ProjectDashboard() {
  return (
    <AttributionProvider>
      <StandardChartsProvider>
        <ProjectDashboardInner />
      </StandardChartsProvider>
    </AttributionProvider>
  );
}

export default withClientorProjects(withFromTo(ProjectDashboard));
